import React, { useCallback, useEffect, useState, KeyboardEvent } from 'react';
import QRCode from 'qrcode';

import {
  Box,
  Button,
  Card,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  makeStyles
} from '@material-ui/core';
import { SwitchLabel } from 'src/components';
import {
  CommonAxiosResponse,
  CreateTemplateResponse,
  Quotation,
  TemplateDetails
} from 'src/types';
import {
  SaveAltOutlined,
  CameraAlt,
  RotateLeft,
  Clear,
  Add,
  Beenhere,
  AddToQueue,
  Link,
  FileCopy,
  PersonAdd,
  Camera
} from '@material-ui/icons';

import PrintIcon from '@material-ui/icons/Print';
import { Alert } from '@material-ui/lab';
import { usePermissions, useSnackBar } from 'src/hooks';
import { copyToClipboard, transformEcommQuotationLink } from 'src/utils';
import { SplitButtons } from 'src/components/buttons/SplitButtons';
import { srpOptions } from 'src/constants';
import { SRPOption } from 'src/types/srp-options';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import { unwrapResult } from '@reduxjs/toolkit';
import { slices, useAppDispatch, useAppSelector } from 'src/redux';
import { validateVoucherThunk } from 'src/redux/slices/voucher/thunks';

interface Props {
  showCleanSrpQuote: boolean;
  hasCompatibility: boolean;
  newSrpValue: SRPOption;
  srpValue?: SRPOption;
  showSRP?: boolean;
  hasValidProduct?: boolean;
  template: TemplateDetails | undefined;
  isCategoryLoading?: boolean;
  isSelectedTemplate: boolean;
  onHandlePrintQuotation: () => void;
  applySrp: () => void;
  onAddInStockRow: () => void;
  setNewSrpValue: (value: SRPOption) => void;
  // eslint-disable-next-line no-undef
  onAddCustomRow: (item?: Partial<Quotation>) => void;
  onResetNew: () => void;
  onScreenShot: (version?: string) => void;
  onRemoveEmptyFields: () => void;
  onCheckAvailability: () => void;
  onToggleShowCleanSRPSwitch: (value: boolean) => void;
  onToggleHasCompatibility: (value: boolean) => void;
  onSaveTemplate: () => void;
  onDeleteTemplate: () => void;
  isPCBuild: number;
  setIsPCBuild: (value: number) => void;
  isBoost: number;
  setIsBoost: (value: number) => void;
  onAddCustomer: () => void;
  onGenerateAddToCartLink: () => any; // intended any for some reasone can't enter CommonAxiosResponse<GetTemplateDetailsResponse> | undefined
  products?: Quotation[];
}

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: theme.spacing(15),
    padding: theme.spacing(2)
  },
  buttonContainer: {
    margin: theme.spacing(1.5)
  },
  switchContainer: {
    margin: theme.spacing(0)
  }
}));

const { selectors: quotationSelectors } = slices.quotations;

export const FooterButtons = ({
  showSRP = false,
  hasCompatibility = true,
  showCleanSrpQuote = false,
  template,
  isCategoryLoading,
  isSelectedTemplate,
  isBoost,
  isPCBuild,
  srpValue,
  newSrpValue,
  hasValidProduct,
  onHandlePrintQuotation,
  applySrp,
  onToggleHasCompatibility,
  setNewSrpValue,
  onAddInStockRow,
  onAddCustomRow,
  onResetNew,
  onRemoveEmptyFields,
  onScreenShot,
  onCheckAvailability,
  onToggleShowCleanSRPSwitch,
  onSaveTemplate,
  onDeleteTemplate,
  setIsPCBuild,
  setIsBoost,
  onGenerateAddToCartLink,
  onAddCustomer,
  products = []
}: Props) => {
  const classes = useStyles();
  const snackBar = useSnackBar();
  const dispatch = useAppDispatch();
  const totalAmount = useAppSelector(quotationSelectors.selectTotalAmount);
  const hasVoucherOnQuotationTable = products.some(
    (item) => item.product?.category_name === 'VOUCHER'
  );
  const { canSaveTemplateQuotation } = usePermissions();

  const [walkinUriQR, setWalkInUriQR] = useState<string>('');
  const [walkInFullLink, setWalkInFullLink] = useState<string>('');
  const [disable, setDisable] = useState<boolean>(true);
  const [selectedVoucherCode, setSelectedVoucherCode] = useState<string>('');
  const [isVoucherValidating, setIsVoucherValidating] = useState<boolean>(
    false
  );

  const getTransactionNoUrl = useCallback(() => {
    try {
      if (walkInFullLink) {
        QRCode.toDataURL(walkInFullLink, { errorCorrectionLevel: 'H' })
          .then((url) => setWalkInUriQR(url))
          .catch((err) => console.error(err));
      }
    } catch (error) {
      console.error(error);
    }
  }, [walkInFullLink]);

  const onGenerateLink = async () => {
    // TODO: ADD LOADER. Clear qr code and link immediately even before generating link
    setWalkInUriQR('');
    setWalkInFullLink('');

    const generateResponse:
      | CommonAxiosResponse<CreateTemplateResponse>
      | undefined = await onGenerateAddToCartLink();

    const addToCartLink = generateResponse?.originalData?.add_to_user_cart_link;
    if (addToCartLink) {
      alert(
        'Please ensure that the customer already has account on pcworth.com before sending the link.'
      );
      setWalkInFullLink(addToCartLink);
      return;
    }

    setWalkInFullLink('');
    snackBar.show({
      severity: 'error',
      message: `There was an error generating the add to cart link`,
      useSound: true
    });
  };

  const validateVoucher = async (items: Quotation[]) => {
    // Only pass items here that are not yet voided
    // const cleanedItems = items?.filter((item) => !item?.isDeleted);
    const cleanedItems = items;

    try {
      const validateVoucher = unwrapResult(
        await dispatch(
          validateVoucherThunk({
            voucher_code: selectedVoucherCode || '',
            total_amount: totalAmount,
            products: cleanedItems
              .filter(
                (item) =>
                  item.product?.product_id &&
                  item.product?.category_id !== 35 &&
                  item.product?.category_id !== 52
              )
              .map((item) => ({
                product_id: item.product?.product_id,
                product_name: item.product?.product_name,
                amount: item.product?.retail_price,
                category_id: item.product?.category_id,
                quantity: Number(item.quantity || 0)
              }))
          })
        )
      );

      if (!validateVoucher?.success) {
        snackBar.show({
          severity: 'error',
          message:
            validateVoucher?.errors?.message ||
            validateVoucher?.message ||
            'Voucher is not valid'
        });
        return;
      } else {
        onAddCustomRow({
          custom: true,
          editable: true,
          quantity: 1,
          product: {
            category_name: 'VOUCHER',
            product_name: selectedVoucherCode,
            retail_price: -(
              validateVoucher?.originalData?.data?.total_discounted_price || 0
            )
          }
        });
        setSelectedVoucherCode('');
        snackBar.show({
          severity: 'success',
          message: validateVoucher.message
        });
      }
    } catch (error) {
      console.error('Error apply voucher:', error);
      snackBar.show({
        severity: 'error',
        message: 'An error occurred while apply the voucher'
      });
    }
  };

  const handleKeyDown = async (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();

      if (isVoucherValidating || !selectedVoucherCode) {
        return;
      }

      setIsVoucherValidating(true);
      try {
        await validateVoucher(products || []);
      } finally {
        setIsVoucherValidating(false);
      }
    }
  };

  useEffect(getTransactionNoUrl, [getTransactionNoUrl]);

  return (
    <Card
      elevation={5}
      className={classes.root}
      style={{ position: 'relative', margin: 10, padding: 1 }}
    >
      {isCategoryLoading ? (
        <div
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(240, 240, 240, 0.5)',
            display: 'grid',
            placeContent: 'center',
            zIndex: 1000
          }}
        />
      ) : null}
      <div>
        <SwitchLabel
          disabled
          className={classes.switchContainer}
          label="Allow Compatibility"
          checked={hasCompatibility}
          onChange={(v) => onToggleHasCompatibility(v)}
        />
        {/* <SwitchLabel
          className={classes.switchContainer}
          label="Show SRP comparison"
          checked={showSRP ? true : false}
          onChange={(v) => onToggleShowSRPSwitch(v)}
          disabled={!isEmpty(isCategoryLoading)}
        /> */}
        <SwitchLabel
          className={classes.switchContainer}
          label="PC Build"
          checked={isPCBuild ? true : false}
          onChange={(v) => setIsPCBuild(v ? 1 : 0)}
        />
        <SwitchLabel
          className={classes.switchContainer}
          label="Boost"
          checked={isBoost ? true : false}
          onChange={(v) => setIsBoost(v ? 1 : 0)}
        />
        {showSRP ? (
          <SwitchLabel
            className={classes.switchContainer}
            label="Show clean SRP Quote"
            checked={showCleanSrpQuote ? true : false}
            onChange={(v) => onToggleShowCleanSRPSwitch(v)}
          />
        ) : null}
      </div>
      {showSRP && (
        <Box p={1}>
          <Alert severity="info">
            Printing a quotation slip will display the SRP of items instead of
            promo price
          </Alert>
        </Box>
      )}
      <div style={{ paddingRight: 20 }}>
        <Grid container spacing={1}>
          <Grid item md={3}>
            <Button
              className={classes.buttonContainer}
              startIcon={<Add />}
              onClick={onAddInStockRow}
              color="primary"
              variant="outlined"
              fullWidth
            >
              {`Add "In-Stock" Row`}
            </Button>
          </Grid>

          <Grid item md={3}>
            <Button
              className={classes.buttonContainer}
              startIcon={<AddToQueue />}
              onClick={() => onAddCustomRow()}
              color="primary"
              variant="outlined"
              fullWidth
            >
              {`Add "Custom / Pre Order" Row`}
            </Button>
          </Grid>

          <Grid item md={3}>
            <Button
              className={classes.buttonContainer}
              startIcon={<Camera />}
              onClick={() => onScreenShot('v2')}
              color="primary"
              variant="outlined"
              fullWidth
            >
              Take Screenshot V2
            </Button>
          </Grid>

          <Grid item md={3}>
            <Button
              className={classes.buttonContainer}
              startIcon={<CameraAlt />}
              onClick={() => onScreenShot()}
              color="primary"
              variant="text"
              size="small"
            >
              Old Screenshot V1
            </Button>
          </Grid>

          <Grid item md={3}>
            <Button
              className={classes.buttonContainer}
              startIcon={<Beenhere />}
              onClick={onCheckAvailability}
              color="primary"
              variant="contained"
              fullWidth
            >
              Check Availability
            </Button>
          </Grid>

          <Grid item md={3}>
            <Button
              className={classes.buttonContainer}
              startIcon={<PrintIcon />}
              onClick={onHandlePrintQuotation}
              color="primary"
              variant="contained"
              disabled={isSelectedTemplate}
              fullWidth
            >
              Print Quotation Slip
            </Button>
          </Grid>
        </Grid>
      </div>

      <div style={{ marginLeft: 10 }}>
        <SplitButtons
          initial_val={newSrpValue.name}
          title="Current Pricing"
          options={Object.values(srpOptions)} // TODO: make options name value
          disableButton={
            disable || !hasValidProduct || newSrpValue === srpValue
          }
          onChange={(option: SRPOption) => {
            setNewSrpValue(option);
            option !== srpValue && setDisable(false);
          }}
          onApplyClick={applySrp}
        />
      </div>

      <div style={{ marginLeft: 10, width: '18%', marginTop: 20 }}>
        <TextField
          disabled={hasVoucherOnQuotationTable}
          placeholder="Enter Voucher Code"
          variant="outlined"
          fullWidth
          label={'Enter Voucher Code'}
          size="small"
          value={selectedVoucherCode}
          onChange={(e) => setSelectedVoucherCode(e.target.value)}
          onKeyDown={handleKeyDown}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Box
                  sx={{
                    height: 24,
                    mx: 1,
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <Divider orientation="vertical" flexItem />
                </Box>
                <IconButton
                  disabled={
                    !selectedVoucherCode ||
                    products.filter(
                      (item) =>
                        item.product?.product_id &&
                        item.product?.category_id !== 35 &&
                        item.product?.category_id !== 52
                    ).length === 0 ||
                    totalAmount === 0
                  }
                  onClick={() => {
                    validateVoucher(products || []);
                  }}
                  style={{
                    color:
                      selectedVoucherCode && products && totalAmount
                        ? 'green'
                        : 'gray',
                    padding: '5px'
                  }}
                >
                  <AddCircleIcon />
                </IconButton>
              </InputAdornment>
            )
          }}
        />
      </div>

      <div>
        <Button
          className={classes.buttonContainer}
          startIcon={<RotateLeft />}
          onClick={onResetNew}
          color="secondary"
          variant="outlined"
        >
          Reset to New
        </Button>
        <Button
          className={classes.buttonContainer}
          startIcon={<Clear />}
          onClick={onRemoveEmptyFields}
          color="secondary"
          variant="outlined"
        >
          Remove Empty Fields
        </Button>
      </div>

      <div>
        {template && (
          <Button
            className={classes.buttonContainer}
            startIcon={<Clear />}
            onClick={onDeleteTemplate}
            color="secondary"
            variant="outlined"
          >
            Delete
          </Button>
        )}
        <Button
          className={classes.buttonContainer}
          startIcon={<SaveAltOutlined />}
          onClick={onSaveTemplate}
          color="primary"
          variant="outlined"
          disabled={!canSaveTemplateQuotation}
        >
          Save Template
        </Button>
      </div>

      <Divider />

      <Button
        className={classes.buttonContainer}
        startIcon={<PersonAdd />}
        onClick={onAddCustomer}
        color="primary"
        variant="contained"
      >
        CREATE CUSTOMER
      </Button>

      <Divider />

      <div>
        {walkinUriQR ? <img src={walkinUriQR} alt="WALK IN QR CODE" /> : null}

        {walkInFullLink ? (
          <Typography
            color="primary"
            className={classes.buttonContainer}
            variant="h6"
          >
            <Box display="flex" alignItems="center" style={{ gap: 5 }}>
              <Typography variant="h6" style={{ fontWeight: 'bold' }}>
                Add to Cart:
              </Typography>
              {walkInFullLink}
            </Box>
          </Typography>
        ) : null}

        {walkInFullLink ? (
          <Typography
            color="primary"
            className={classes.buttonContainer}
            variant="h6"
          >
            <Box display="flex" alignItems="center" style={{ gap: 5 }}>
              <Typography variant="h6" style={{ fontWeight: 'bold' }}>
                Page:
              </Typography>
              {transformEcommQuotationLink(walkInFullLink)}
            </Box>
          </Typography>
        ) : null}

        {walkInFullLink ? (
          <Typography
            color="error"
            className={classes.buttonContainer}
            variant="h4"
          >
            Always make sure customer has pcworth.com account and that they get
            the proper items from their cart
          </Typography>
        ) : null}

        {walkInFullLink ? (
          <Button
            className={classes.buttonContainer}
            startIcon={<FileCopy />}
            onClick={() => copyToClipboard(walkInFullLink)}
            color="primary"
            variant="outlined"
          >
            Copy - Add to Cart Link
          </Button>
        ) : null}

        {walkInFullLink ? (
          <Button
            className={classes.buttonContainer}
            startIcon={<FileCopy />}
            onClick={() => copyToClipboard(transformEcommQuotationLink(walkInFullLink))}
            color="primary"
            variant="outlined"
          >
            Copy - Page Link
          </Button>
        ) : null}

        <Button
          className={classes.buttonContainer}
          startIcon={<Link />}
          onClick={onGenerateLink}
          color="primary"
          variant="contained"
        >
          GENERATE QUOTATION LINK
        </Button>
      </div>
    </Card>
  );
};

export const QuotationFooterButtons = React.memo(FooterButtons);
